import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { ShelfContextField } from "@/components/ui/autoform/components/custom/ShelfContextField";
// import { Datetime } from "@/components/forms/schemas/datetime";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getChangedValues, isFormDirty } from "@/lib/utils";
import { SortingField } from "@/pages/shelves/components/sorting-field";
import { useShelfRepository } from "@/pages/shelves/hooks/use-shelf-repository";
import {
  ShelvesEditFormType,
  shelfEditSchema,
} from "@/pages/shelves/schemas/shelves.edit.form";

const schemaProvider = new ZodProvider(shelfEditSchema);

export const ShelvesEdit = () => {
  const { action } = useParsed();
  const { get, update, create } = useShelfRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<ShelvesEditFormType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();

  const setTitle = useDocumentTitle();

  // CRUD hooks
  useEffect(() => {
    if (!id) {
      setWarnWhen(true);
      return;
    }
    setLoading(true);
    get(id!.toString())
      .then((response) => {
        const data = shelfEditSchema.parse(response);
        setValues(data);
        setTitle("Edit " + data.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get shelf",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<ShelvesEditFormType, any, ShelvesEditFormType>,
  ) => {
    form.watch((changedValues) => {
      if (!values) return;
      const changed = getChangedValues(
        changedValues,
        values as ShelvesEditFormType,
      );
      setWarnWhen(Object.keys(changed).length > 0);
    });
  };

  const onSubmit = async (values: ShelvesEditFormType) => {
    setWarnWhen(false);
    try {
      if (!id) {
        await create(values);
      } else {
        await update(values);
      }
    } catch {
      setWarnWhen(true);
    }
  };
  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        {(values || action === "create") && (
          <AutoForm
            defaultValues={values}
            schema={schemaProvider}
            onFormInit={initializeForm}
            onSubmit={onSubmit}
            {...injectCustomAutoFormComponents({
              formComponents: {
                shelfContext: ShelfContextField,
                sorting: SortingField,
              },
              uiComponents: {},
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
